import { UserButton, useUser } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleFlag } from "react-circle-flags";
import { useTranslation } from "react-i18next";

import { ClubOnboardingForm } from "@/components/club/club-onboarding";
import { PlayerOnboardingForm } from "@/components/player/player-onboarding";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { getUserContextRecovery } from "@/context/user-context-error";
import { useTRPC } from "@/lib/trpc";
import { toggleLanguage } from "@/lib/utils";
import { SelectSignupRole } from "@/pages/auth/signup/select-signup-role-page";

export function OnboardingPage() {
  const { user } = useUser();
  const { i18n, t } = useTranslation();
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  // La redirection quand le profil existe déjà appartient à `OnboardingRoute`.
  // Ici on ne choisit que le formulaire, et `users.role` (écrit à la création du
  // compte) prime sur `unsafeMetadata.role` : une metadata Clerk vide ou perdue
  // afficherait sinon un écran sans aucun formulaire.
  const { data: me, status, error } = useQuery(trpc.user.getMe.queryOptions(undefined, {
    retryOnMount: false,
  }));
  const completeSignup = useMutation(trpc.user.completeSignup.mutationOptions({
    onSuccess: async () => {
      await queryClient.invalidateQueries(trpc.user.getMe.queryFilter());
    },
  }));
  const unsafeRole = typeof user?.unsafeMetadata.role === "string" ? user.unsafeMetadata.role : undefined;
  const role = me?.role ?? unsafeRole;

  return (
    <div className="relative flex h-dvh w-full items-center justify-center bg-neutral-800 text-white">
      <div className="absolute right-4 top-4">
        <UserButton />
      </div>
      <div className="absolute right-14 top-4">
        <Tooltip>
          <TooltipTrigger asChild>
            <button onClick={() => toggleLanguage(i18n)} type="button">
              {i18n.language === "en-US" ? <CircleFlag countryCode="fr" className="size-7 rounded-full border-2 border-white" /> : <CircleFlag countryCode="gb" className="size-7 rounded-full border-2 border-white" />}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            {i18n.language === "en-US" ? "Switch to French" : "Switch to English"}
          </TooltipContent>
        </Tooltip>
      </div>

      {status === "error" && getUserContextRecovery(error) === "onboarding" && (
        <div className="flex flex-col items-center gap-4">
          <SelectSignupRole disabled={completeSignup.isPending} onSelectRole={role => completeSignup.mutate({ role })} />
          {completeSignup.error && <p className="text-red-300 max-w-sm text-center" role="alert">{t("auth.complete-account-error")}</p>}
        </div>
      )}
      {status === "error" && getUserContextRecovery(error) !== "onboarding" && <p role="alert">{t("error-occured-msg")}</p>}
      {status !== "error" && role === "player" && <PlayerOnboardingForm />}
      {status !== "error" && role === "club" && <ClubOnboardingForm />}
    </div>
  );
}
